import api from "./api";
import { ok, isoUtc, nameOf, reasonOf, getMe, can, ORG_SCOPES } from "./core";
import { getErrorCode, getErrorMessage } from "../utils/errors";

export const INVITATION_STATUS = {
  PENDING_DELIVERY: { label: "Sending", tone: "info" },
  SENT: { label: "Email sent", tone: "primary" },
  DELIVERY_FAILED: { label: "Email failed", tone: "danger" },
  ACCEPTED: { label: "Accepted", tone: "success" },
  EXPIRED: { label: "Link expired", tone: "warning" },
  CANCELLED: { label: "Replaced / cancelled", tone: "neutral" },
};

export async function canInvite() {
  const me = await getMe();
  return can(me, "identity.manage", ["SYSTEM_ALL"]) || can(me, "account.invite", ORG_SCOPES);
}

const toInvitation = (i) => ({
  id: i.invitation_id,
  employeeId: i.employee_id,
  employeeName: i.employee_name || nameOf(i.employee_id),
  username: i.username,
  email: i.recipient_email,
  status: i.invitation_status,
  statusLabel: INVITATION_STATUS[i.invitation_status]?.label || i.invitation_status,
  tone: INVITATION_STATUS[i.invitation_status]?.tone || "neutral",
  deliveryStatus: i.delivery_status,
  attempts: i.attempt_count,
  sentAt: isoUtc(i.sent_at),
  expiresAt: isoUtc(i.expires_at),
  acceptedAt: isoUtc(i.accepted_at),
  createdAt: isoUtc(i.created_at),
  lastError: i.last_error,
  canResend: i.invitation_status !== "ACCEPTED",
});

export async function listInvitations({ employeeId, status, latestOnly = true } = {}) {
  const { data } = await api.get("/account-invitations", { params: { employee_id: employeeId, status, latest_only: latestOnly, limit: 200 } });
  return ok(data.map(toInvitation));
}

export async function inviteEmployee({ employeeId, username, reason }) {
  const { data } = await api.post("/account-invitations", {
    employee_id: employeeId,
    username: String(username || "").trim().toLowerCase(),
    reason: reasonOf(reason, "Login invitation sent"),
  });
  return ok(toInvitation(data));
}

export async function resendInvitation(employeeId, reason) {
  const { data } = await api.post(`/account-invitations/${employeeId}/resend`, { reason: reasonOf(reason, "Login invitation resent") });
  return ok(toInvitation(data));
}

// The token can be the e-mail token or (preferred) the exchange token from exchangeInvitationLink().
export const acceptInvitation = ({ token, newPassword }) =>
  api.post("/auth/accept-invitation", { token, new_password: newPassword });

// [NEW] GET /auth/accept-invitation/validate?token=... - read-only check when the invitation link is opened.
export const validateInvitationLink = (token) =>
  api.get("/auth/accept-invitation/validate", { params: { token } });

// [NEW] POST /auth/accept-invitation/exchange - swaps the e-mail token for a short-lived exchange token.
// After this call the original e-mail link no longer works.
export const exchangeInvitationLink = (token) =>
  api.post("/auth/accept-invitation/exchange", { token });

export const suggestUsername = (employee) => String((employee?.email || "").split("@")[0] || employee?.name || "")
  .toLowerCase().replace(/[^a-z0-9_.@-]+/g, ".").replace(/^\.+|\.+$/g, "").slice(0, 100);

const USERNAME_RE = /^[a-z0-9_.@-]{3,100}$/;

// [NEW] Bulk invite: sends one invitation per employee using the same POST /account-invitations call as the
// single "Invite" button, with the username taken from the e-mail address (same rule as the single dialog).
// outcomes: "sent" | "skipped" (already has a login / no e-mail) | "failed". Runs a few requests at a time and
// stops early if the server says invitation e-mail is not configured (every call would fail the same way).
export async function inviteEmployeesBulk(employees, { onProgress, concurrency = 3 } = {}) {
  const results = [];
  const used = new Set();
  let aborted = null;
  let next = 0;
  let done = 0;

  const pickUsername = (emp) => {
    let u = suggestUsername(emp);
    if (!USERNAME_RE.test(u)) u = String(emp.id || "").toLowerCase().replace(/[^a-z0-9_.@-]+/g, ".");
    if (used.has(u)) u = `${u}.${String(emp.id || "").toLowerCase()}`.slice(0, 100);
    used.add(u);
    return u;
  };

  const record = (emp, outcome, message = "") => {
    results.push({ id: emp.id, name: emp.name, email: emp.email || "", outcome, message });
    done += 1;
    onProgress?.({ done, total: employees.length });
  };

  const inviteOne = async (emp) => {
    if (!emp.email) { record(emp, "skipped", "No official email on record."); return; }
    let username = pickUsername(emp);
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        await inviteEmployee({ employeeId: emp.id, username, reason: "Login invitation sent (bulk)" });
        record(emp, "sent");
        return;
      } catch (err) {
        const code = getErrorCode(err);
        if (code === "ACCOUNT_EXISTS") { record(emp, "skipped", "Already has a login."); return; }
        if (code === "INVITATION_UNAVAILABLE") {
          aborted = "Invitation emails are not configured on the server yet. Ask the administrator to set up SMTP and ACCOUNT_INVITATION_URL.";
          record(emp, "failed", aborted);
          return;
        }
        // Username already taken by someone else: retry once with the employee ID added.
        if (attempt === 0 && err?.response?.status === 409) {
          username = `${username}.${String(emp.id).toLowerCase()}`.slice(0, 100);
          continue;
        }
        record(emp, "failed", getErrorMessage(err));
        return;
      }
    }
  };

  const worker = async () => {
    while (next < employees.length && !aborted) {
      const emp = employees[next]; next += 1;
      await inviteOne(emp);
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(concurrency, employees.length)) }, worker));

  // Anything not attempted because the run was aborted.
  employees.slice(next).forEach((emp) => results.push({ id: emp.id, name: emp.name, email: emp.email || "", outcome: "failed", message: "Not attempted." }));
  return { results, aborted };
}

// [NEW] Bulk resend: POST /account-invitations/{employee_id}/resend for every row, a few at a time.
// Each resend replaces the earlier link, so older links stop working. Stops early if invitation e-mail is not configured.
export async function resendInvitationsBulk(rows, { onProgress, concurrency = 3 } = {}) {
  const results = [];
  let aborted = null;
  let next = 0;
  let done = 0;
  const record = (row, outcome, message = "") => {
    results.push({ id: row.employeeId, name: row.employeeName, email: row.email || "", outcome, message });
    done += 1;
    onProgress?.({ done, total: rows.length });
  };
  const worker = async () => {
    while (next < rows.length && !aborted) {
      const row = rows[next]; next += 1;
      try {
        await resendInvitation(row.employeeId, "Login invitation resent (bulk)");
        record(row, "sent");
      } catch (err) {
        const code = getErrorCode(err);
        if (code === "ACCOUNT_EXISTS") record(row, "skipped", "Already accepted or has a login.");
        else if (code === "INVITATION_UNAVAILABLE") {
          aborted = "Invitation emails are not configured on the server yet. Ask the administrator to set up SMTP and ACCOUNT_INVITATION_URL.";
          record(row, "failed", aborted);
        } else record(row, "failed", getErrorMessage(err));
      }
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(concurrency, rows.length)) }, worker));
  rows.slice(next).forEach((row) => results.push({ id: row.employeeId, name: row.employeeName, email: row.email || "", outcome: "failed", message: "Not attempted." }));
  return { results, aborted };
}
